import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';

const EmployerView = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Mock data - replace with actual API call
  const employer = {
    id: 'EMP-001',
    companyName: 'TechCorp Inc.',
    companyEmail: 'contact@techcorp.com',
    companyPhone: '+1 (555) 123-4567',
    website: 'https://techcorp.com',
    industry: 'Technology',
    employeeSize: '100-200',
    foundedYear: '2015',
    taxId: '12-3456789',
    status: 'active',
    verified: true,
    featured: true,
    
    // Address
    address: '123 Tech Street',
    city: 'New York',
    state: 'NY',
    country: 'United States',
    zipCode: '10001',
    
    // Admin Details
    adminName: 'John Smith',
    adminEmail: 'john.smith@techcorp.com',
    adminPhone: '+1 (555) 123-4567',
    
    // Contact Person Details
    contactName: 'Sarah Johnson',
    contactEmail: 'sarah.j@techcorp.com',
    contactPhone: '+1 (555) 123-4568',
    contactPosition: 'HR Manager',
    
    // Company Description
    aboutCompany: `
      <h2>About TechCorp Inc.</h2>
      <p>TechCorp is a leading technology company specializing in innovative software solutions for businesses worldwide. Founded in 2015, we have grown to become a trusted partner for organizations seeking digital transformation.</p>
      
      <h3>Our Mission</h3>
      <p>To empower businesses with cutting-edge technology solutions that drive growth and efficiency.</p>
      
      <h3>What We Do</h3>
      <ul>
        <li>Custom Software Development</li>
        <li>Cloud Solutions & Migration</li>
        <li>AI and Machine Learning</li>
        <li>Digital Transformation Consulting</li>
      </ul>
    `,
    
    // Social Media
    linkedin: 'https://linkedin.com/company/techcorp',
    twitter: 'https://twitter.com/techcorp',
    facebook: 'https://facebook.com/techcorp',
    
    // Stats
    jobsPosted: 15,
    activeJobs: 8,
    totalApplications: 234,
    hiredCandidates: 45,
    joinDate: '2023-06-15',
    lastActive: '2024-01-20'
  };

  const recentJobs = [
    { id: 1, title: 'Senior Frontend Developer', status: 'active', applications: 23, posted: '2024-01-15' },
    { id: 2, title: 'DevOps Engineer', status: 'active', applications: 18, posted: '2024-01-12' },
    { id: 3, title: 'Product Manager', status: 'active', applications: 15, posted: '2024-01-10' },
    { id: 4, title: 'UX Designer', status: 'closed', applications: 34, posted: '2023-12-20' },
  ];

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', label: 'Active' },
      inactive: { color: 'bg-gray-100 text-gray-800', label: 'Inactive' }
    };
    const config = statusConfig[status] || statusConfig.inactive;
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>{config.label}</span>;
  };

  const getJobStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', label: 'Active' },
      closed: { color: 'bg-red-100 text-red-800', label: 'Closed' }
    };
    const config = statusConfig[status] || statusConfig.active;
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>{config.label}</span>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-start">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/hpanel/employers')}
              className="text-gray-600 hover:text-gray-800"
            >
              <i className="fas fa-arrow-left text-xl"></i>
            </button>
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <h2 className="text-2xl font-bold text-white">{employer.companyName}</h2>
                {employer.verified && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                    <i className="fas fa-check-circle mr-1"></i> Verified
                  </span>
                )}
                {employer.featured && (
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
                    <i className="fas fa-star mr-1"></i> Featured
                  </span>
                )}
              </div>
              <p className="text-gray-400">{employer.industry} • {employer.employeeSize} employees • {employer.id}</p>
            </div>
          </div>
          <div className="flex space-x-3">
            <Link
              to={`/hpanel/employers/edit/${employer.id}`}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center"
            >
              <i className="fas fa-edit mr-2"></i>
              Edit Employer
            </Link>
            <button className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center">
              <i className="fas fa-envelope mr-2"></i>
              Send Message
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Company Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Company Information Card */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Company Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="text-sm font-medium text-gray-500">Company Name</label>
                <p className="text-gray-900">{employer.companyName}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Industry</label>
                <p className="text-gray-900">{employer.industry}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Company Email</label>
                <p className="text-gray-900">{employer.companyEmail}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Company Phone</label>
                <p className="text-gray-900">{employer.companyPhone}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Website</label>
                <p className="text-gray-900">
                  <a href={employer.website} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700">
                    {employer.website}
                  </a>
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Employee Size</label>
                <p className="text-gray-900">{employer.employeeSize} employees</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Founded Year</label>
                <p className="text-gray-900">{employer.foundedYear}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Tax ID</label>
                <p className="text-gray-900">{employer.taxId}</p>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500 mb-2 block">About Company</label>
              <div 
                className="prose max-w-none text-gray-700"
                dangerouslySetInnerHTML={{ __html: employer.aboutCompany }}
              />
            </div>
          </div>

          {/* Address Information Card */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Address Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-gray-500">Street Address</label>
                <p className="text-gray-900">{employer.address}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">City</label>
                <p className="text-gray-900">{employer.city}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">State/Province</label>
                <p className="text-gray-900">{employer.state}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Country</label>
                <p className="text-gray-900">{employer.country}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">ZIP/Postal Code</label>
                <p className="text-gray-900">{employer.zipCode}</p>
              </div>
            </div>
          </div>

          {/* Recent Jobs Card */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Recent Job Postings</h3>
              <Link to="/hpanel/jobs" className="text-primary-600 text-sm font-medium">
                View All Jobs ({employer.jobsPosted})
              </Link>
            </div>
            
            <div className="space-y-4">
              {recentJobs.map((job) => (
                <div key={job.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{job.title}</h4>
                    <p className="text-sm text-gray-600">Posted on {formatDate(job.posted)}</p>
                  </div>
                  <div className="flex items-center space-x-4">
                    {getJobStatusBadge(job.status)}
                    <span className="text-sm text-gray-600">{job.applications} applications</span>
                    <Link
                      to={`/hpanel/jobs/${job.id}`}
                      className="text-primary-600 hover:text-primary-800"
                    >
                      <i className="fas fa-eye"></i>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column - Stats & Contacts */}
        <div className="space-y-6">
          {/* Company Stats Card */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Company Stats</h3>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                    <i className="fas fa-briefcase text-blue-600"></i>
                  </div>
                  <span className="text-sm text-gray-600">Jobs Posted</span>
                </div>
                <span className="text-lg font-semibold text-gray-800">{employer.jobsPosted}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                    <i className="fas fa-play-circle text-green-600"></i>
                  </div>
                  <span className="text-sm text-gray-600">Active Jobs</span>
                </div>
                <span className="text-lg font-semibold text-green-600">{employer.activeJobs}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                    <i className="fas fa-file-alt text-purple-600"></i>
                  </div>
                  <span className="text-sm text-gray-600">Total Applications</span>
                </div>
                <span className="text-lg font-semibold text-gray-800">{employer.totalApplications}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center mr-3">
                    <i className="fas fa-user-check text-orange-600"></i>
                  </div>
                  <span className="text-sm text-gray-600">Hired Candidates</span>
                </div>
                <span className="text-lg font-semibold text-gray-800">{employer.hiredCandidates}</span>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-800">
                    {employer.totalApplications > 0 ? Math.round((employer.hiredCandidates / employer.totalApplications) * 100) : 0}%
                  </div>
                  <div className="text-sm text-gray-600">Hiring Success Rate</div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information Card */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Contact Information</h3>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Admin Contact</label>
                <p className="text-gray-900 font-medium">{employer.adminName}</p>
                <p className="text-gray-600 text-sm">{employer.adminEmail}</p>
                <p className="text-gray-600 text-sm">{employer.adminPhone}</p>
              </div>
              
              <div className="pt-4 border-t border-gray-200">
                <label className="text-sm font-medium text-gray-500">HR Contact</label>
                <p className="text-gray-900 font-medium">{employer.contactName}</p>
                <p className="text-gray-600 text-sm">{employer.contactPosition}</p>
                <p className="text-gray-600 text-sm">{employer.contactEmail}</p>
                <p className="text-gray-600 text-sm">{employer.contactPhone}</p>
              </div>
            </div>
          </div>

          {/* Social Media Card */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Social Media</h3>
            
            <div className="space-y-3">
              {employer.linkedin && (
                <div className="flex items-center">
                  <i className="fab fa-linkedin text-blue-600 w-5 mr-3"></i>
                  <a href={employer.linkedin} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 text-sm">
                    LinkedIn
                  </a>
                </div>
              )}
              {employer.twitter && (
                <div className="flex items-center">
                  <i className="fab fa-twitter text-blue-400 w-5 mr-3"></i>
                  <a href={employer.twitter} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 text-sm">
                    Twitter
                  </a>
                </div>
              )}
              {employer.facebook && (
                <div className="flex items-center">
                  <i className="fab fa-facebook text-blue-600 w-5 mr-3"></i>
                  <a href={employer.facebook} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 text-sm">
                    Facebook
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions Card */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Quick Actions</h3>
            
            <div className="space-y-2">
              <button className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg flex items-center">
                <i className="fas fa-envelope mr-3 text-gray-400"></i>
                Send Welcome Email
              </button>
              <button className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg flex items-center">
                <i className="fas fa-file-invoice mr-3 text-gray-400"></i>
                Generate Report
              </button>
              <button className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg flex items-center">
                <i className="fas fa-briefcase mr-3 text-gray-400"></i>
                View All Jobs
              </button>
              <button className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg flex items-center">
                <i className="fas fa-ban mr-3"></i>
                Deactivate Account
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployerView;